import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from './lib/theme'
import { ToastProvider } from './components/ui/Toast'
import { ConfirmProvider } from './components/ui/Confirm'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { AuthProvider } from './lib/auth'
import { App } from './App'
import { BUILD } from './lib/build'
import { isStaleChunkError, reloadOnceForStaleBuild } from './lib/staleChunk'
import { startUsageReporting } from './lib/usage.runtime'
import './index.css'

/**
 * A lazily loaded chunk that no longer exists, caught before React ever sees it.
 *
 * Vite raises `vite:preloadError` when a chunk fails to load, and a failed dynamic import
 * that nothing awaited surfaces as an unhandled rejection — neither reaches an error
 * boundary. Both mean the same thing: this tab is asking for files a deploy replaced.
 * Reloading is the whole fix, and doing it here means the person never sees anything.
 */
const recover = (error: unknown) => {
  if (!isStaleChunkError(error)) return
  reloadOnceForStaleBuild(BUILD.sha, sessionStorage, () => window.location.reload())
}

window.addEventListener('vite:preloadError', (e) => {
  recover((e as Event & { payload?: unknown }).payload)
})
window.addEventListener('unhandledrejection', (e) => recover(e.reason))

// Starts the usage tally's periodic flush. Never stopped: it lives as long as the tab.
startUsageReporting()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Outermost, so a failure in the providers themselves still renders something a
        person can act on rather than a blank page. */}
    <ErrorBoundary area="the tracker">
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>,
)
